import { useMemo, useState } from "react";
import {
  Card,
  Form,
  Input,
  InputNumber,
  DatePicker,
  Select,
  Radio,
  Button,
  message,
  Space,
  Typography,
} from "antd";
import dayjs from "dayjs";
import { MinusCircleOutlined, PlusOutlined } from "@ant-design/icons";
import { useNavigate } from "react-router-dom";

import { API_BASE_URL } from "shared/api/config";

type BusinessType = "PICSHOT" | "PICO_KIDS" | "GROCERIES";
type PaymentType = "CASH" | "ACCOUNT" | "QPAY";

const paymentOptions = [
  { label: "Бэлэн", value: "CASH" },
  { label: "Данс", value: "ACCOUNT" },
  { label: "QPay", value: "QPAY" },
];

const unitOptions = [
  { label: "кг", value: "kg" },
  { label: "г", value: "g" },
  { label: "л", value: "l" },
  { label: "мл", value: "ml" },
  { label: "ш", value: "pcs" },
  { label: "хайрцаг", value: "box" },
  { label: "уут", value: "bag" },
];

const picshotCategories = [
  "Хэвлэлийн цаас",
  "Жааз",
  "Canvas",
  "Хор",
  "Дэвсгэр",
  "Тоног төхөөрөмж",
  "Түрээс",
  "Цалин",
  "Цэвэрлэгээ",
  "Бусад",
];

const picoKidsCategories = [
  "Мах",
  "Ногоо",
  "Нарийн ногоо",
  "Гурил",
  "Будаа",
  "Гоймон",
  "Амтлагч",
  "Ундаа",
  "Тоглоом",
  "Цэвэрлэгээ",
  "Ажилчдын цалин",
  "Түрээс",
  "Цахилгаан / Ус",
  "Gaz",
  "Бусад",
];

// GROCERIES дээр item сонгох жагсаалт (хүсвэл өөрөөр бүлэглэж болно)

async function apiCreateExpense(payload: {
  businessType: BusinessType;
  expenseCategory: string;
  amount: number;
  paymentType: PaymentType;
  date: string;
  description?: string;
  supplier?: string;
}) {
  const res = await fetch(`${API_BASE_URL}/expense`, {
    method: "POST",
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify(payload),
  });

  const data = await res.json().catch(() => ({}));

  if (!res.ok) {
    const msg = (data && (data.message || data.error)) || `HTTP ${res.status}`;
    throw new Error(msg);
  }
  return data;
}

export default function ExpenseCreate() {
  const [form] = Form.useForm();
  const [business, setBusiness] = useState<BusinessType>("PICSHOT");
  const navigate = useNavigate();

  const categories = useMemo(() => {
    if (business === "PICSHOT") return picshotCategories;
    // PICO_KIDS болон GROCERIES -> picoKidsCategories ашиглая (хүсвэл тусад нь гаргаж болно)
    return picoKidsCategories;
  }, [business]);

  const onBusinessChange = (val: BusinessType) => {
    setBusiness(val);
    form.setFieldsValue({
      expenseCategory: undefined,
      // GROCERIES солигдоход items default үүсгэж өгье
      groceriesItems:
        val === "GROCERIES"
          ? [{ itemName: undefined, unit: "kg", qty: 0, unitPrice: 0 }]
          : undefined,
      amount: undefined,
    });
  };

  // GROCERIES items-оос нийт дүн бодох (Form-ийн утгаас)
  const computeGroceriesTotal = () => {
    const rows = (form.getFieldValue("groceriesItems") || []) as any[];
    return rows.reduce((sum, r) => {
      const qty = Number(r?.qty || 0);
      const unitPrice = Number(r?.unitPrice || 0);
      return sum + qty * unitPrice;
    }, 0);
  };

  const buildGroceriesDescription = () => {
    const rows = (form.getFieldValue("groceriesItems") || []) as any[];
    const lines = rows
      .map((r, i) => {
        const name = (r?.itemName || "").trim();
        const unit = r?.unit || "";
        const qty = Number(r?.qty || 0);
        const unitPrice = Number(r?.unitPrice || 0);
        if (!name && !qty && !unitPrice) return null;

        const unitLabel =
          unit === "kg"
            ? "кг"
            : unit === "g"
            ? "г"
            : unit === "l"
            ? "л"
            : unit === "ml"
            ? "мл"
            : unit === "pcs"
            ? "ш"
            : unit === "box"
            ? "хайрцаг"
            : unit === "bag"
            ? "уут"
            : unit;

        const lineTotal = qty * unitPrice;
        return `${i + 1}) ${
          name || "Нэргүй"
        } — ${qty} ${unitLabel} × ${unitPrice.toLocaleString()}₮ = ${lineTotal.toLocaleString()}₮`;
      })
      .filter(Boolean);

    return lines.join("\n");
  };

  const onFinish = async (values: any) => {
    try {
      // GROCERIES үед amount-ийг автоматаар бодно
      let amount = values.amount;
      let description = values.description || "";

      if (business === "GROCERIES") {
        const total = computeGroceriesTotal();
        if (total <= 0) {
          message.error(
            "Нийт дүн 0-ээс их байх ёстой (тоо, нэгж үнэ оруулна уу)."
          );
          return;
        }
        amount = total;

        const itemsText = buildGroceriesDescription();
        description = [itemsText, description].filter(Boolean).join("\n\n");
      }

      const payload = {
        businessType: business,
        expenseCategory: values.expenseCategory,
        amount: Number(amount || 0),
        paymentType: values.paymentType as PaymentType,
        date: values.date.format("YYYY-MM-DD"),
        description,
        supplier: values.supplier || "",
      };

      await apiCreateExpense(payload);

      message.success("Зардал амжилттай бүртгэгдлээ");
      form.resetFields();
      form.setFieldsValue({ date: dayjs(), paymentType: "CASH" });
      navigate("/expenselist");
      // business хэвээр байлгая
      if (business === "GROCERIES") {
        form.setFieldsValue({
          groceriesItems: [
            { itemName: undefined, unit: "kg", qty: 0, unitPrice: 0 },
          ],
        });
      }
    } catch (e: any) {
      message.error(e?.message || "Алдаа гарлаа. Дахин оролдоно уу.");
    }
  };

  // GROCERIES total-ийг UI дээр realtime харуулах (Form.useWatch ашиглахгүйгээр энгийнээр)
  const groceriesRows = Form.useWatch("groceriesItems", form);
  const groceriesTotal = useMemo(() => {
    return ((groceriesRows || []) as any[]).reduce((sum, r) => {
      const qty = Number(r?.qty || 0);
      const unitPrice = Number(r?.unitPrice || 0);
      return sum + qty * unitPrice;
    }, 0);
  }, [groceriesRows]);

  return (
    <div style={{ maxWidth: 820, margin: "0 auto" }}>
      <Card title="Зардал бүртгэл" bordered>
        <Form
          form={form}
          layout="vertical"
          onFinish={onFinish}
          initialValues={{
            businessType: "PICSHOT",
            date: dayjs(),
            paymentType: "CASH",
            groceriesItems: [
              { itemName: undefined, unit: "kg", qty: 0, unitPrice: 0 },
            ],
          }}
        >
          {/* Business chooser */}
          <Form.Item label="Бизнес сонгох">
            <Radio.Group
              value={business}
              onChange={(e) => onBusinessChange(e.target.value)}
              optionType="button"
              buttonStyle="solid"
            >
              <Radio.Button value="PICSHOT">PicShot</Radio.Button>
              <Radio.Button value="PICO_KIDS">Pico Kids Cafe</Radio.Button>
              <Radio.Button value="GROCERIES">Pico Kids GROCERIES</Radio.Button>
            </Radio.Group>
          </Form.Item>

          {/* Category */}
          <Form.Item
            label="Зардлын төрөл"
            name="expenseCategory"
            rules={[{ required: true, message: "Зардлын төрөл сонгоно уу" }]}
          >
            <Select placeholder="Сонгох">
              {categories.map((c) => (
                <Select.Option key={c} value={c}>
                  {c}
                </Select.Option>
              ))}
            </Select>
          </Form.Item>

          {/* Supplier */}
          {(business === "PICO_KIDS" || business === "GROCERIES") && (
            <Form.Item label="Нийлүүлэгч (optional)" name="supplier">
              <Input placeholder="Ж: GS25, Nomin, supplier нэр..." />
            </Form.Item>
          )}

          {/* ✅ GROCERIES: нэмэлт мөртэй хүнс */}
          {business === "GROCERIES" && (
            <div
              style={{
                padding: 12,
                border: "1px solid #eee",
                borderRadius: 10,
                marginBottom: 12,
              }}
            >
              <Typography.Title level={5} style={{ marginTop: 0 }}>
                Нэмэлт хүнсний мөрүүд
              </Typography.Title>

              <Form.List name="groceriesItems">
                {(fields, { add, remove }) => (
                  <>
                    {fields.map(({ key, name, ...restField }) => (
                      <Space
                        key={key}
                        align="baseline"
                        style={{ display: "flex", marginBottom: 10 }}
                        wrap
                      >
                        <Form.Item
                          {...restField}
                          name={[name, "itemName"]}
                          rules={[
                            { required: true, message: "Зүйл оруулна уу" },
                          ]}
                        >
                          <Input
                            placeholder="Ж: Мах, Ногоо, Ундаа..."
                            style={{ width: 220 }}
                          />
                        </Form.Item>

                        <Form.Item
                          {...restField}
                          name={[name, "unit"]}
                          rules={[{ required: true, message: "Хэмжээ" }]}
                        >
                          <Select
                            placeholder="Хэмжээ"
                            style={{ width: 120 }}
                            options={unitOptions}
                          />
                        </Form.Item>

                        <Form.Item
                          {...restField}
                          name={[name, "qty"]}
                          rules={[{ required: true, message: "Тоо" }]}
                        >
                          <InputNumber
                            min={0}
                            placeholder="Тоо"
                            style={{ width: 120 }}
                          />
                        </Form.Item>

                        <Form.Item
                          {...restField}
                          name={[name, "unitPrice"]}
                          rules={[{ required: true, message: "Нэгж үнэ" }]}
                        >
                          <InputNumber
                            min={0}
                            placeholder="Нэгж үнэ"
                            style={{ width: 140 }}
                          />
                        </Form.Item>

                        <MinusCircleOutlined onClick={() => remove(name)} />
                      </Space>
                    ))}

                    <Button
                      type="dashed"
                      onClick={() =>
                        add({
                          itemName: undefined,
                          unit: "kg",
                          qty: 0,
                          unitPrice: 0,
                        })
                      }
                      block
                      icon={<PlusOutlined />}
                    >
                      Нэмэлт мөр нэмэх
                    </Button>
                  </>
                )}
              </Form.List>

              <div
                style={{
                  marginTop: 10,
                  display: "flex",
                  justifyContent: "flex-end",
                }}
              >
                <Typography.Text strong>
                  Нийт дүн: {groceriesTotal.toLocaleString()}₮
                </Typography.Text>
              </div>
            </div>
          )}

          {/* Amount - GROCERIES дээр нууж/disabled болгоно */}
          {business !== "GROCERIES" && (
            <Form.Item
              label="Дүн (₮)"
              name="amount"
              rules={[{ required: true, message: "Дүн оруулна уу" }]}
            >
              <InputNumber
                style={{ width: "100%" }}
                min={0}
                step={100}
                placeholder="Ж: 50000"
              />
            </Form.Item>
          )}

          {/* Payment */}
          <Form.Item
            label="Төлбөрийн хэлбэр"
            name="paymentType"
            rules={[{ required: true, message: "Төлбөрийн хэлбэр сонгоно уу" }]}
          >
            <Select options={paymentOptions} />
          </Form.Item>

          {/* Date */}
          <Form.Item
            label="Огноо"
            name="date"
            rules={[{ required: true, message: "Огноо сонгоно уу" }]}
          >
            <DatePicker style={{ width: "100%" }} />
          </Form.Item>

          {/* Description */}
          <Form.Item label="Тайлбар (optional)" name="description">
            <Input.TextArea rows={3} placeholder="Ж: баримт №..., хүргэлт..." />
          </Form.Item>

          <Button type="primary" htmlType="submit" block>
            Хадгалах
          </Button>
        </Form>
      </Card>
    </div>
  );
}
